import React from 'react';
import { observer } from 'mobx-react';
import * as mobx from 'mobx';

// Helpers
import { checkValidity, closeDrawer, getURLSegment, getParam, handleSwitchInputKeys } from '../../helpers/helpers.js';

// Translations
import translate from '../Translate.js';

// Components
import Step from '../Common/Step';
import EditSection from './EditSection';

// Dependencies
import moment from 'moment';
import { FeatureFlag } from 'react-launch-darkly';

const ExamException = observer(class ExamException extends React.Component {
  constructor() {
    super();
    this.setStep = this.setStep.bind(this);
    this.nextStep = this.nextStep.bind(this);
    this.prevStep = this.prevStep.bind(this);
    this.renderDefault = this.renderDefault.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleApprove = this.handleApprove.bind(this);
    this.complete = this.complete.bind(this);
    this.momentFormat = this.momentFormat.bind(this);
  }

  componentWillMount() {
    const { ExamStore, SettingsStore, ProctorStore } = this.props.store;
    let configurationSid = this.props.routing.location.query.accommodation || this.props.routing.location.query.changeRequest;

    if(this.props.approval) {
      configurationSid = getURLSegment(this, 6);
    }

    this.state = {
      step: 1,
      enrollmentSid: this.props.routing.location.query.enrollment,
      configurationSid,
      examSid: null,
      scheduled: false,
      skippable: false,
      locked: true,
      groups: []
    }

    // Get the exam sid from the URL
    let examSid = null;
    let sessionSid = null;

    if(this.props.approval) {
      examSid = getURLSegment(this, 4);
    } else {
      examSid = getURLSegment(this, 2);
      sessionSid = getParam('session', window.location.href)
    }

    if (examSid && sessionSid) {
      const callback = (response) => {
        if (response.data.status === 'Scheduled') {
          this.setState({ scheduled: true});
          ExamStore.resetExamSession();
        }
      }
      ExamStore.getExamSession(examSid, sessionSid, callback)
    }

    this.setState({
      examSid
    });

    if(typeof this.state.configurationSid !== 'undefined') {
      // If a configurationSid is present, get that configuration data otherwise remove the data
      this.getConfiguration(examSid);

      this.setState({
        skippable: true
      })
    } else {
      // Otherwise use the default exam data
      ExamStore.setConfigurationData(examSid);

      // If this is a cross listed student, remove proctor types
      if(this.props.crossListed) {
        ExamStore.configuration.proctorTypes = [];
      }
    }

    this.getFilteredProctorTypes();
    ProctorStore.getProctorGroups(this);

    // Make sure we have the default exam data
    if(!this.props.store.ExamStore.exam.title) {
      let courseSid = getURLSegment(this, 8);

      if(courseSid) {
        ExamStore.getExam(examSid, courseSid);
      } else {
        ExamStore.getExam(examSid);
      }
    }

    // Get insitution settings so we can only show allowed permitted items
    SettingsStore.getInstituteSettings(this);

    // Make sure other exam attachments don’t interfere
    ExamStore.deleteAttachments = [];
    ExamStore.examAttachments = null;
  }

  componentWillUnmount() {
    if(this.props.approval) {
      this.props.store.ExamStore.resetExam();
    }
  }

  resetConfiguration() {
    this.props.store.ExamStore.resetConfigurationData();
  }

  getConfiguration(examSid) {
    let courseSid = getURLSegment(this, 8);

    if(courseSid) {
      this.props.store.ExamStore.getConfigurationData(examSid, this.state.configurationSid, courseSid);
    } else {
      this.props.store.ExamStore.getConfigurationData(examSid, this.state.configurationSid);
    }
  }

  getFilteredProctorTypes() {
    this.props.store.SessionStore.getProctorGroupTypes();
  }

  setStep(e, step) {
    if(e) {
      e.preventDefault();
    }

    this.setState({
      step
    });
  }

  nextStep() {
    this.setState({
      step: this.state.step + 1
    });
  }

  prevStep() {
    this.setState({
      step: this.state.step - 1
    });
  }

  handleInput(name, e, isConfiguration, date, time, isCheckbox, array, skipFormat, categorySid) {
    let data = null;
    let value = null;

    if(e) {
      value = e.target.value;
    }

    // Make sure boolean values are boolean
    if(value === 'true') {
      value = true;
    }
    if(value === 'false') {
      value = false;
    }

    // If we’re updating a date, we need to format it
    if(date) {
      date = moment(date).format('YYYY-MM-DD Z');
      value = moment(`${date} ${time}`, 'YYYY-MM-DD Z HH:mm').toISOString();
    }

    // If it’s a checkbox, get the checked state
    if(isCheckbox) {
      value = e.target.checked;
    }

    // If the data provided is an array
    if(array) {
      value = array;
    }

    if (categorySid) {
      data = {
        [name]: {
          [categorySid]: value
        }
      };
    } else {
      data = {
        [name]: value
      };
    }

    if(isConfiguration) {
      this.props.store.ExamStore.updateConfigurationData(data, categorySid);
    } else {
      this.props.store.ExamStore.updateExamData(data);
    }
  }

  handleSubmit(exit) {
    const { translation } = this.props;
    const { ExamStore, SessionStore } = this.props.store;
    const { configuration, exam } = ExamStore;

    this.setState({
      submitted: null
    });

    // Don’t submit until the last step
    if(exit === true) {
      this.setStep(null, translation.steps.length);
    } else if(this.state.step === translation.steps.length) {
      let callback = null;

      callback = (examSid, configurationSid) => {
        this.handleAttachments(examSid, configurationSid);
      };

      // Make sure at least 1 proctor type has been chosen
      if((configuration.proctorTypes && configuration.proctorTypes.length > 0) || this.props.crossListed) {
        // Trigger the tally loading view
        SessionStore.tallyLoad = true;

        if(!this.state.configurationSid) {
          // If we’re not editing, create a configuration
          SessionStore.tallySteps.push('CreateConfiguration');

          ExamStore.createConfiguration(
            this.state.examSid,
            this.state.enrollmentSid,
            callback,
            this.props.defaultConfigSid
          );
        } else {
          // Otherwise, update the configuration
          SessionStore.tallySteps.push('UpdateConfiguration');

          let courseSid = getURLSegment(this, 8);

          if(courseSid) {
            const { query } = this.props.routing.location;
            let callback2 = null;

            if(!query.exception || query.exception === 'false') {
              callback2 = () => {
                ExamStore.updateExam(
                  this.state.examSid,
                  null,
                  true,
                  courseSid
                );
              }
            }

            ExamStore.updateConfiguration(
              this.state.examSid,
              this.state.configurationSid,
              callback,
              courseSid,
              callback2
            );
          } else {
            ExamStore.updateConfiguration(
              this.state.examSid,
              this.state.configurationSid,
              callback
            )
          }
        }
        // Add tally step for new attachments
        if(
          (exam.type.toLowerCase() === 'online' && ExamStore.examAttachments) ||
          (exam.type.toLowerCase() === 'written' && ExamStore.examFiles)
        ) {
          SessionStore.tallySteps.push('UpdateExamAttachments');
        }

        // Add tally step for removed attachments
        if(ExamStore.deleteAttachments.length > 0) {
          SessionStore.tallySteps.push('DeleteExamAttachments');
        }
      } else {
        // Let the user know that no proctor types are selected
        const dialogContent = translation.proctor_warning;
        SessionStore.showDialog(dialogContent);
      }
    } else {
      this.nextStep();
    }
  }

  handleAttachments(examSid, configurationSid) {
    const { ExamStore } = this.props.store;
    const { exam } = ExamStore;

    // Handle new attachments and deleted attachments
    if(
      (exam.type.toLowerCase() === 'online' && ExamStore.examAttachments) ||
      (exam.type.toLowerCase() === 'written' && ExamStore.examFiles)
    ) {
      // New attachments
      let callback = null;

      if(ExamStore.deleteAttachments.length === 0) {
        // If there aren’t any to delete, run
        // the complete function after uploading
        if(this.props.approval) {
          callback = this.handleApprove;
        } else {
          callback = this.complete;
        }
      } else {
        // Otherwise, run the delete endpoint
        callback = () => this.deleteAttachments(examSid, configurationSid);
      }

      // Upload API call
      let uploadData = null;

      if(exam.type.toLowerCase() === 'online') {
        uploadData = {
          attachments: ExamStore.examAttachments
        };
      } else {
        uploadData = {
          examFiles: ExamStore.examFiles
        };
      }

      ExamStore.updateExamAttachments(
        examSid,
        configurationSid,
        uploadData,
        callback
      );
    } else if(ExamStore.deleteAttachments.length > 0) {
      this.deleteAttachments(examSid, configurationSid);
    } else {
      // Otherwise, just finish
      if(this.props.approval) {
        this.handleApprove();
      } else {
        this.complete();
      }
    }
  }

  deleteAttachments(examSid, configurationSid) {
    const { ExamStore } = this.props.store;
    const { exam } = ExamStore;

    // Delete API call
    let deleteData = null;

    // Changes here may need to also be made to ExamEdit.js, just check it

    if(exam.type.toLowerCase() === 'online') {
      deleteData = {
        attachments: ExamStore.deleteAttachments
      };
    } else {
      deleteData = {
        examFiles: ExamStore.deleteAttachments
      };
    }

    let callback = null;

    if(this.props.approval) {
      callback = this.handleApprove;
    } else {
      callback = this.complete;
    }

    ExamStore.deleteExamAttachments(
      examSid,
      configurationSid,
      deleteData,
      callback
    );
  }

  handleApprove() {
    const { ExamStore } = this.props.store;

    const data = {
      "approved": true,
      "locked": this.state.locked
    };

    const courseSid = getURLSegment(this, 8);

    ExamStore.updateExamApproval(this.state.examSid, this.state.configurationSid, data, this.complete, courseSid);
  }

  complete(sid) {
    const _this = this;
    const { pathname } = this.props.routing.location;
    const { translation } = this.props;

    // Set a small timeout so the transition
    // from tally loader to next screen
    // isn’t as jarring
    setTimeout(function(){
      _this.props.routing.push({
        pathname,
        search: null
      });

      if(!_this.props.approval && !_this.props.changeRequest) {
        closeDrawer(null, _this.props.parent);
        _this.props.getEnrollments(_this.state.examSid);
        _this.props.store.SessionStore.showToaster(translation.configuration_saved, 'success');
      } else if(!_this.props.approval && _this.props.changeRequest) {
        closeDrawer(null, _this.props.parent);
        _this.props.getEnrollments(_this.state.examSid);
        _this.props.getExam(_this.state.examSid);
        _this.props.store.SessionStore.showToaster(translation.change_request_saved, 'success');
      } else {
        closeDrawer(null, _this.props.parent, getURLSegment(_this, 1));
        _this.props.store.ExamStore.getExamApprovals();
        _this.props.store.SessionStore.showToaster(translation.configuration_approved, 'success');
      }
    }, 500);
  }

  renderDefault(field, format, checkbox, checkboxVal) {
    const { translation } = this.props;
    const { ExamStore } = this.props.store;

    // Some of the exam properties are nested in
    // an object called configuration, while others
    // are not, so we need to figure out which
    // situation applies to this property
    let obj = ExamStore.exam;
    let defaultVal = null;

    if (!field) {
      return obj;
    }

    let prop, props = field.split('.');

    for (var i = 0, iLen = props.length - 1; i < iLen; i++) {
      prop = props[i];

      var candidate = obj[prop];
      if (candidate !== undefined) {
        obj = candidate;
      } else {
        break;
      }
    }

    defaultVal = obj[props[i]];

    // Output the default value
    if(format) {
      // If there’s a format specified (e.g. format minute as hours)
      return (
        <span className="fields__default">Default: {format(defaultVal)}</span>
      )
    } else if(checkbox) {
      // If the field is a checkbox
      let checkboxState = translation.disabled;
      let convertedDefault = mobx.toJS(defaultVal);

      // If the default value is an array, check if this value is included in it
      // or if it’s a string, check if it is equal to this value
      if(
          (convertedDefault && typeof convertedDefault === 'object' && convertedDefault.indexOf(checkboxVal) > -1) ||
          (typeof convertedDefault === 'string' && convertedDefault === checkboxVal) ||
          (convertedDefault && typeof convertedDefault === 'boolean')
        ) {
        checkboxState = translation.enabled;
      }

      return (
        <span className="fields__default">Default: {checkboxState}</span>
      )
    } else {
      return (
        <span className="fields__default">Default: {defaultVal}</span>
      )
    }
  }

  momentFormat(date) {
    const { userTimezone } = this.props.store.SessionStore;
    return moment.utc(date).tz(userTimezone).format('M/D/YYYY [at] h:mma z');
  }

  // Add a class to conditionally style fields that differ from the default
  defaultDiff(field) {
    const { ExamStore } = this.store;
    const examDefault = ExamStore.exam.configuration[field] || '';
    const configDefault = ExamStore.configuration[field] || '';

    // If the exam configuration value (default value) is different from this configuration value
    if(examDefault.toString() !== configDefault.toString()) {
      return 'field--diff';
    }

    return '';
  }

  flagAutomatedOnly = (automatedOnly, exam, disabledFields, query, configuration) => {
    return(
      <EditSection
        step={this.state.step}
        nextStep={this.nextStep}
        prevStep={this.prevStep}
        renderDefault={this.renderDefault}
        defaultDiff={this.defaultDiff}
        momentFormat={this.momentFormat}
        exception={
          this.props.approval ?
            query.exception && query.exception === 'true' ?
            true :
            false :
          true
        }
        {...this.props}
        exam={exam}
        handleInput={this.handleInput}
        customConfiguration={configuration}
        approval={this.props.approval}
        readonly={this.props.readonly}
        disabledFields={disabledFields}
        submitted={this.state.submitted}
        automatedOnly={automatedOnly}
        proctorGroups={this.state.groups}
        scheduled={this.state.scheduled}
      />
    )
  }

  render() {
    const { translation } = this.props;
    const { ExamStore, SessionStore } = this.props.store;
    const { exam, configuration } = ExamStore;
    const { query } = this.props.routing.location;
    let steps = [...translation.steps];

    let headingKey = 'heading';

    if(query.exception && query.exception === 'true') {
      headingKey = 'heading_review_exception';
    } else if(query.exception && query.exception === 'false') {
      headingKey = 'heading_review_exam';
    }

    const invalidCallback = () => {
      this.setState({
        submitted: true
      });
    }

    if((typeof this.state.configurationSid !== 'undefined' && configuration) || typeof this.state.configurationSid === 'undefined') {
      if(!SessionStore.loading && !SessionStore.tallyLoad) {
        let disabledFields = null;

        if(exam.configuration.apiExamId && this.props.approval && (!query.exception || query.exception === 'false')) {
          disabledFields = [
            'title',
            'duration',
            'password',
            'attempts',
            'examOpenDate',
            'examCloseDate',
            'url'
          ]
        }

        return(
          <div className="drawer__content">
            <header className="drawer__header">
              <div className="wrap">
                {!this.props.changeRequest &&
                  <h1 className="drawer__title">{translation[headingKey]}</h1>
                }

                {this.props.changeRequest &&
                  <h1 className="drawer__title">{translation.heading_change}</h1>
                }
              </div>
            </header>

            <div className="wrap block-form">
              <Step
                step={this.state.step}
                setStep={this.setStep}
                data={steps}
                skippable={this.state.skippable}
              />

              <form
                className="exam-form panel"
                onSubmit={(e) => checkValidity(e, this.handleSubmit, 'form.exam-form', invalidCallback)}
                noValidate
              >
                <div className="panel__header">
                  <h3 className="panel__title"><legend>{steps[this.state.step - 1]}</legend></h3>
                </div>

                <div className="panel__body">
                  <FeatureFlag
                    flagKey="automated-only"
                    renderFeatureCallback={automatedOnly => {
                      return this.flagAutomatedOnly(automatedOnly, exam, disabledFields, query, configuration);
                    }}
                    renderDefaultCallback={automatedOnly => {
                      return this.flagAutomatedOnly(automatedOnly, exam, disabledFields, query, configuration);
                    }}
                  />

                  {!this.props.readonly &&
                    <div className="panel__actions actions">
                      {this.state.configurationSid && !this.props.approval && this.state.step !== translation.steps.length &&
                        <button
                          className="actions__btn actions__btn--secondary actions__btn--offset"
                          type="button"
                          onClick={
                            (e) => checkValidity(
                              e,
                              () => this.handleSubmit(true),
                              'form.exam-form'
                            )
                          }
                        >
                          {translation.save_and_exit}
                        </button>
                      }

                      {this.state.step > 1 &&
                        <button
                          className="actions__btn actions__btn--secondary"
                          type="button"
                          onClick={this.prevStep}
                        >
                          {translation.back}
                        </button>
                      }

                      {((this.props.approval && this.state.step < steps.length) || !this.props.approval) &&
                        <button
                          className="actions__btn actions__btn--primary"
                          type="submit"
                        >
                          {this.state.step !== translation.steps.length &&
                            translation.continue
                          }

                          {this.state.step === translation.steps.length &&
                            translation.confirm
                          }
                        </button>
                      }

                      {this.props.approval && this.state.step === steps.length &&
                        <button
                          className="actions__btn actions__btn--primary"
                        >
                          {translation.approve}
                        </button>
                      }

                      {this.props.approval && this.state.step === steps.length &&
                        <div className="actions__opt">
                          <label htmlFor="config-locked">{translation.lock}</label>

                          <input
                            type="checkbox"
                            id="config-locked"
                            className="switch-input"
                            ref={(input) => {this.locked = input}}
                            onChange={() => this.setState({locked: !this.state.locked})}
                            onKeyDown={handleSwitchInputKeys}
                            defaultChecked={true}
                          />

                          <label className="switch-label" htmlFor="config-locked"><span className="meta">{translation.lock}</span></label>
                        </div>
                      }
                    </div>
                  }
                </div>
              </form>
            </div>
          </div>
        );
      }
    }

    return null;
  }
})

export default translate('ExamException')(ExamException);



// WEBPACK FOOTER //
// ./src/app/components/ExamEdit/ExamException.js